import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Only allow same-origin relative paths as redirect targets.
 * SignInPage.tsx:18 and AuthCallbackPage.tsx:59 validate query/metadata
 * return paths here before passing them to React Router/Clerk.
 * Browsers normalize backslashes and strip C0 controls while parsing URLs;
 * a slash-prefix check alone accepts off-origin destinations such as /\\host.
 */
export function safeRedirect(value: string | null): string | undefined {
  if (!value) return undefined;
  if (!value.startsWith('/') || value.startsWith('//')) return undefined;
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (character === '\\' || code <= 0x1f || code === 0x7f) return undefined;
  }
  const base = 'https://overlayqa.invalid';
  try {
    if (new URL(value, base).origin !== base) return undefined;
  } catch {
    return undefined;
  }
  return value;
}

/**
 * True for a localhost / private-network / .local host. These are reachable
 * only from the user's own browser, so they're captured through the extension
 * (our servers can't reach them). Accepts a bare hostname or a full URL.
 */
export function isLocalUrl(value: string): boolean {
  try {
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    const { hostname } = new URL(withProtocol);
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) return true;
    if (hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname === '[::1]') return true;
    if (hostname.endsWith('.local')) return true;
    if (/^10\./.test(hostname)) return true;
    if (/^192\.168\./.test(hostname)) return true;
    if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return true;
    return false;
  } catch {
    return false;
  }
}

/** Preserve the compact labels and date cutoffs of existing dashboard surfaces. */
export function timeAgo(dateStr: string | Date, options: {
  justNow?: string;
  showSeconds?: boolean;
  round?: boolean;
  dateAfterDays?: number;
} = {}): string {
  const seconds = (Date.now() - new Date(dateStr).getTime()) / 1000;
  const integer = options.round ? Math.round : Math.floor;
  if (seconds < 60) return options.showSeconds ? `${integer(seconds)}s ago` : (options.justNow ?? 'just now');
  if (seconds < 3600) return `${integer(seconds / 60)}m ago`;
  if (seconds < 86400) return `${integer(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86400);
  if (options.dateAfterDays !== undefined && !(days < options.dateAfterDays)) {
    return new Date(dateStr).toLocaleDateString();
  }
  return `${days}d ago`;
}
